import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    IconButton,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    LinearProgress
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import { toast } from "react-toastify";

const UploadDocumentModal = ({
    isOpen,
    onClose,
    onUpload,
    loading,
    progress = 0
}) => {
    const [file, setFile] = useState(null);
    const [docType, setDocType] = useState('rc_book');

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;

        const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png",
            "image/jpg",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];

        if (allowedTypes.includes(selected.type) || selected.type.startsWith('image/')) {
            setFile(selected);
        } else {
            toast.error("Please upload a valid PDF, Image, or Word document.");
        }
    };

    const handleSubmit = () => {
        if (!file) {
            toast.error("Please select a file first");
            return;
        }
        onUpload(file, docType);
    };

    const handleClose = () => {
        setFile(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, textAlign: "left !important", position: 'relative' }}>
                <Typography
                    sx={{
                        fontFamily: "Roboto Slab !important",
                        fontWeight: "500 !important",
                        fontSize: "32px !important",
                        lineHeight: "56px !important",
                        color: "#000000 !important",
                    }}
                >
                    Upload Document
                </Typography>
                <IconButton
                    aria-label="Close"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <IoClose size={22} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ textAlign: "center" }}>
                <FormControl component="fieldset" sx={{ width: '100%', mb: 3, textAlign: 'left' }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500, color: '#212121' }}>Select Document Type</FormLabel>
                    <RadioGroup
                        row
                        value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                    >
                        <FormControlLabel
                            value="rc_book"
                            control={<Radio sx={{ color: '#0063F7', '&.Mui-checked': { color: '#0063F7' } }} />}
                            label="RC Book"
                        />
                        <FormControlLabel
                            value="aadhaar"
                            control={<Radio sx={{ color: '#0063F7', '&.Mui-checked': { color: '#0063F7' } }} />}
                            label="Aadhaar Card"
                        />
                    </RadioGroup>
                </FormControl>

                <Box
                    sx={{
                        border: "2px dashed #ccc",
                        borderRadius: "4px",
                        p: 4,
                        mb: 2,
                        backgroundColor: "#FAFAFA",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 180
                    }}
                >
                    <FilePresentIcon sx={{ fontSize: 40, color: "#757575", mb: 1 }} />
                    <Typography
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "18px",
                            lineHeight: "26px",
                            mb: 2,
                            textAlign: 'center'
                        }}
                    >
                        Drag and drop or click to upload your document
                    </Typography>

                    <Button
                        component="label"
                        sx={{
                            color: "#0063F7",
                            textTransform: "none",
                            fontSize: "16px",
                            "&:hover": {
                                textDecoration: "underline",
                                backgroundColor: "inherit"
                            }
                        }}
                    >
                        Choose file
                        <input
                            hidden
                            type="file"
                            accept=".pdf,.doc,.docx,image/*"
                            onChange={handleFileChange}
                        />
                    </Button>
                </Box>

                {file && (
                    <Typography
                        sx={{ fontSize: "14px", color: "#666", mt: 1, textAlign: "center" }}
                    >
                        Selected: {file.name}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: "flex-end", gap: 2, py: 2, pr: 3 }}>
                {!loading ? (
                    <>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                width: "140px",
                                height: "50px",
                                borderRadius: "6px",
                                borderColor: "#B00020",
                                color: "#B00020",
                                textTransform: "none",
                                fontSize: "18px",
                                "&:hover": {
                                    borderColor: "#B00020",
                                    backgroundColor: "rgba(176, 0, 32, 0.04)",
                                }
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={handleSubmit}
                            disabled={!file || loading}
                            variant="contained"
                            sx={{
                                width: "140px",
                                height: "50px",
                                borderRadius: "6px",
                                backgroundColor: '#0063F7 !important',
                                textTransform: "none",
                                fontSize: "18px",
                                "&:disabled": {
                                    backgroundColor: "#CCCCCC !important",
                                    color: "#666666 !important",
                                },
                                '&:hover': {
                                    backgroundColor: '#0063F7 !important',
                                    color: '#FFFFFF !important',
                                    boxShadow: 10
                                }
                            }}
                        >
                            {loading ? "Saving..." : "Save"}
                        </Button>
                    </>
                ) : (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                width: "100%",
                                height: "9px",
                                borderRadius: "12px",
                                backgroundColor: "#E5E7EB",
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: "#0063F7"
                                }
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: "16px",
                                lineHeight: "24px",
                                color: "#000000"
                            }}
                        >
                            {progress}%
                        </Typography>
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default UploadDocumentModal;
